// uiarc: command-palette (https://uiarc.dev/r/command-palette.json) görünümü, `cmdk` üzerinde (süzme ve klavye
// cmdk'den: ok tuşları, Enter, Home/End). Arama satırı (54 px, odakta `surface-muted`, yazı varken temizle düğmesi),
// 38 px'lik tek satırlık sonuçlar, etkin satırın arkasında yayla süzülen tek vurgu (`GlideHighlight`) ve liste
// yüksekliğine yayla uyan çerçeve (cmdk'nin `--cmdk-list-height`'ı) uiarc'tandır.
// Uygulanmayanlar: eşleşen harfleri kalın yazma ve süzmede satırların yer değiştirme animasyonu (cmdk süzmeyi kendi
// yapar, eşleşme aralığı vermez), `⌘ K` tuş kapağı (kısayolu çağıran gösterir).
import * as React from 'react'
import { Command as CommandPrimitive, useCommandState } from 'cmdk'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { GlideHighlight } from '@/components/ui/glide-highlight'
import { motionTokens } from '@/lib/motion'
import { messages } from '@/lib/messages'
import { SearchIcon, CheckIcon, XIcon } from 'lucide-react'

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'text-foreground flex size-full flex-col overflow-hidden rounded-[inherit]',
        className,
      )}
      {...props}
    />
  )
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = false,
  ...props
}: Omit<React.ComponentProps<typeof Dialog>, 'children'> & {
  title?: string
  description?: string
  className?: string
  showCloseButton?: boolean
  children: React.ReactNode
}) {
  return (
    <Dialog {...props}>
      <DialogContent
        className={cn(
          'rounded-panel mt-[12vh] mb-auto max-w-[min(calc(100vw-2rem),36rem)] overflow-hidden p-0',
          className,
        )}
        showCloseButton={showCloseButton}
      >
        {/* Base UI: Title/Description Popup içinde olmalı, yoksa dialog etiketlenmez. */}
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  )
}

function CommandInput({
  className,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  const reduced = useReducedMotion()
  const search = useCommandState((state) => state.search)
  const input = React.useRef<HTMLInputElement>(null)
  // cmdk girdisi denetimsizse değeri yerel olarak sıfırlamak için yerel değer ayarlayıcısı + `input` olayı.
  const clear = () => {
    const node = input.current
    if (!node) return
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
    setter?.call(node, '')
    node.dispatchEvent(new Event('input', { bubbles: true }))
    node.focus()
  }
  return (
    <div
      data-slot="command-input-wrapper"
      className="border-border-subtle focus-within:bg-surface-muted ease-standard relative flex min-h-[54px] items-center gap-2.5 border-b px-3.5 py-2 transition-[background-color] duration-(--duration-fast) motion-reduce:transition-none max-[480px]:gap-2 max-[480px]:px-[11px]"
    >
      <span className="text-text-muted grid size-[22px] flex-none place-items-center">
        <SearchIcon className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <CommandPrimitive.Input
        ref={input}
        data-slot="command-input"
        onKeyDown={(event) => {
          onKeyDown?.(event)
          // uiarc: yazı varken Esc önce aramayı temizler, kapatmak için ikinci Esc gerekir.
          if (event.key === 'Escape' && search && !event.defaultPrevented) {
            event.preventDefault()
            event.stopPropagation()
            clear()
          }
        }}
        className={cn(
          'text-foreground placeholder:text-text-muted min-w-0 flex-1 border-0 bg-transparent text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
      <AnimatePresence mode="popLayout" initial={false}>
        {search ? (
          <motion.button
            key="clear"
            type="button"
            aria-label={messages.common.clearSearch}
            onClick={clear}
            className="text-text-muted hover:bg-surface hover:text-foreground active:bg-surface active:text-foreground ease-standard grid size-6 flex-none cursor-pointer place-items-center rounded-[7px] border-0 bg-transparent transition-[color,background-color] duration-(--duration-fast) motion-reduce:transition-none"
            initial={
              reduced
                ? false
                : { opacity: 0, scale: 0.6, filter: `blur(${motionTokens.blur.subtle}px)` }
            }
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={
              reduced
                ? { opacity: 0, transition: { duration: 0 } }
                : {
                    opacity: 0,
                    scale: 0.6,
                    filter: `blur(${motionTokens.blur.subtle}px)`,
                    transition: {
                      duration: motionTokens.duration.instant,
                      ease: motionTokens.ease.standard,
                    },
                  }
            }
            transition={
              reduced
                ? { duration: 0 }
                : {
                    default: motionTokens.spring.snappy,
                    opacity: { duration: motionTokens.duration.instant },
                  }
            }
          >
            <XIcon className="size-[15px]" aria-hidden="true" />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  )
}

function CommandList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        // cmdk liste yüksekliğini `--cmdk-list-height` olarak yazar; çerçeve ona yayla uyar, liste kendi içinde kayar.
        'h-(--cmdk-list-height) max-h-[342px] scroll-py-1.5 overflow-x-hidden overflow-y-auto overscroll-contain outline-none [transition:height_var(--duration-spring)_var(--ease-spring)] motion-reduce:transition-none **:[[cmdk-list-sizer]]:relative **:[[cmdk-list-sizer]]:p-[6px_8px] max-[480px]:**:[[cmdk-list-sizer]]:p-1.5',
        className,
      )}
      {...props}
    >
      <GlideHighlight
        active="[data-selected='true']"
        className="right-2 left-2 rounded-lg max-[480px]:right-1.5 max-[480px]:left-1.5"
      />
      {children}
    </CommandPrimitive.List>
  )
}

function CommandEmpty({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn(
        'text-text-secondary grid justify-items-center gap-1 px-3 pt-[26px] pb-7 text-center text-sm',
        className,
      )}
      {...props}
    >
      <span className="text-text-muted mb-1 grid size-8 place-items-center">
        <SearchIcon className="size-5" strokeWidth={1.75} aria-hidden="true" />
      </span>
      {children}
    </CommandPrimitive.Empty>
  )
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-foreground **:[[cmdk-group-heading]]:leading-body **:[[cmdk-group-heading]]:text-text-muted relative overflow-hidden [&+[cmdk-group]]:mt-1.5 **:[[cmdk-group-heading]]:block **:[[cmdk-group-heading]]:px-2.5 **:[[cmdk-group-heading]]:pt-2 **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('bg-border-subtle -mx-1 h-px', className)}
      {...props}
    />
  )
}

function CommandItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        // Etkin satırın zeminini `GlideHighlight` çizer; satır yalnız simgesini tam kontrasta çıkarır.
        "group/command-item text-foreground [&_svg:not([class*='text-'])]:text-text-muted data-[selected=true]:[&_svg:not([class*='text-'])]:text-foreground relative flex h-[38px] w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 max-[340px]:px-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-colors [&_svg]:duration-(--duration-fast) [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <CheckIcon className="ml-auto opacity-0 group-has-data-[slot=command-shortcut]/command-item:hidden group-data-[checked=true]/command-item:opacity-100" />
    </CommandPrimitive.Item>
  )
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'border-border bg-surface text-text-muted ml-auto inline-grid h-5 min-w-5 flex-none place-items-center rounded-[5px] border px-[5px] text-[11px] leading-none font-medium max-[480px]:hidden',
        className,
      )}
      {...props}
    />
  )
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
